import { resolveId } from '@medplum/core'
import { CodeableConceptDisplay } from '@medplum/react'
import { Incident } from '@seen/core/models/assessments/incident'
import { isOfType } from '@seen/core/utils'
import {
  DataTable,
  DateTimeDisplay,
  Loader,
  PageLayout,
  ParticipantNameplate,
  useNavigateFromClickEvent,
  useSearch,
} from '@seen/react'
import type { ColumnDef } from '@tanstack/react-table'
import { orderBy } from 'lodash-es'
import { type JSX, useMemo } from 'react'

export function IncidentsPage(): JSX.Element {
  const navigate = useNavigateFromClickEvent()

  const { data: incidents, isLoading } = useSearch(
    Incident,
    Incident.baseSearchQuery()
  )

  const columns = useMemo<ColumnDef<Incident>[]>(
    () => [
      {
        id: 'date',
        header: 'Date',
        cell: ({ row }) => <DateTimeDisplay value={row.original.date} />,
      },
      {
        id: 'participant',
        header: 'Participant',
        cell: ({ row }) => {
          const subject = row.original.subject
          if (isOfType(subject, 'Patient')) {
            return <ParticipantNameplate participant={subject} />
          }
          return null
        },
      },
      {
        id: 'type',
        header: 'Type',
        cell: ({ row }) => <CodeableConceptDisplay value={row.original.type} />,
      },
      {
        id: 'title',
        header: 'Title',
        cell: ({ row }) => <span>{row.original.title}</span>,
      },
    ],
    []
  )

  if (isLoading) {
    return <Loader />
  }

  const sortedIncidents = orderBy(incidents ?? [], ['date'], ['desc'])

  return (
    <PageLayout title="Incidents">
      <DataTable
        columns={columns}
        data={sortedIncidents}
        onRowClick={(row, e) => {
          if (row.subject?.reference) {
            const patientId = resolveId(row.subject)
            navigate(`/Patient/${patientId}/incidents/${row.id}`, e)
          }
        }}
      />
    </PageLayout>
  )
}
